import { useTranslation } from "react-i18next";
import {
  IconSparkles,
  IconMessageCircle,
  IconChartDots3,
  IconSearch,
} from "@tabler/icons-react";
import { cn } from "../../../lib/utils";
import type { GptMode } from "../../../domains/gpt/stores/modeStore";

// The mode that PRODUCED an assistant turn (docs/gpt-mode-router.md). `auto` is
// never a resolved engine, so the badge takes the resolved mode
// (`general | evidence`); the type stays `GptMode` so callers can pass a resolved
// value without a second union.
function GptModeBadge({
  mode,
  className,
}: {
  mode: GptMode;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  const { label, icon } = ((): {
    label: string;
    icon: React.JSX.Element;
  } => {
    switch (mode) {
      case "auto":
        return {
          label: t("Auto"),
          icon: <IconSparkles size={12} stroke={2} />,
        };
      case "general":
        return {
          label: t("General"),
          icon: <IconMessageCircle size={12} stroke={2} />,
        };
      case "evidence":
        return {
          label: t("Evidence"),
          icon: <IconChartDots3 size={12} stroke={2} />,
        };
      case "find_publication":
        return {
          label: t("Search"),
          icon: <IconSearch size={12} stroke={2} />,
        };
      default:
        throw new Error(mode satisfies never);
    }
  })();

  return (
    <span
      className={cn(
        // `self-start`: in the answer's flex column it would stretch into a bar.
        "inline-flex self-start items-center gap-1 rounded-full px-2 py-0.5",
        "bg-ds-slate-100 font-dm-sans text-ui-note font-medium text-ds-slate-600",
        className
      )}
    >
      {icon}
      {label}
    </span>
  );
}

export { GptModeBadge };
